import { flow, Instance, types } from 'mobx-state-tree';
import { IUserImageModel, UserImageModel } from './userImage';
import { PickedImage } from '@photosi/expo-photosi-sdk/build/ImageLibrary';
import { logSDKError } from 'src/_shared/logger';
import { BaseModel } from './_base/baseModel';
import { IProductModel } from './product';

export type TUserImageAreaModel = typeof UserImageAreaModel;
export type UserImageAreaModelInstance = Instance<TUserImageAreaModel>;
export interface IUserImageAreaModel extends UserImageAreaModelInstance {}

export const UserImageAreaModel = BaseModel.named('UserImageAreaModel')
  .props({
    id: types.identifier,
    productId: types.string,

    x: types.optional(types.number, 0),
    y: types.optional(types.number, 0),
    width: types.optional(types.number, 0),
    height: types.optional(types.number, 0),
    hasUserImage: types.optional(types.boolean, false),
    userImage: types.maybe(types.safeReference(UserImageModel)),
    clipPath: types.maybe(types.array(types.array(types.number))),
  })
  .views(self => ({
    get userImageWithCropAlert(): IUserImageModel | undefined {
      return self.userImage?.cropAlert ? self.userImage : undefined;
    },
    get product(): IProductModel | undefined {
      return self.root.project.activeProject?.products?.find(product => product.id === self.productId);
    },
  }))
  .actions(self => ({
    setUserImage(userImage?: IUserImageModel) {
      self.userImage = userImage;
    },
    /**
     * adds images to userImageArea with current product configuration
     */
    addImages: flow(function* addImage(param: { files: PickedImage[] }) {
      self.root.dynamicModal.activeModal?.setIsUploadingPhoto(true);

      try {
        yield self.sdkProject?.addImagesToProduct(self.productId, param.files, self.id);
      } catch (error) {
        // Lowered here and not in a `finally`: on success the flag has to stay up until
        // UserImageAdded lands, because that handler is what closes the picker modal.
        // Without this the spinner never stops — and LoadingStep hides the error banner
        // while it is up, so a failed retry looks like one that simply never ends.
        self.root.dynamicModal.activeModal?.setIsUploadingPhoto(false);
        logSDKError(error, { productId: self.productId, files: param.files, userImageAreaId: self.id });
      }
    }),
  }));
